import React, { useState, useEffect } from 'react'
import { 
  Typography, 
  Box, 
  Button, 
  Grid, 
  Alert,
  Tabs,
  Tab,
  CircularProgress,
} from '@mui/material'
import { SidebarLayout } from '@/components/Layout'
import { ProtectedRoute } from '@/components/common'
import { Add } from '@mui/icons-material'
import { Link } from 'react-router-dom'
import { ListingCard } from '@/components/Listings'
import { LoadingSpinner } from '@/components/common'
import { Listing } from '@/types'
import { ListingsService } from '@/utils/listings-service'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`listings-tabpanel-${index}`}
      aria-labelledby={`listings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

export default function ListingsIndex() {
  const [listings, setListings] = useState<Listing[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tabValue, setTabValue] = useState(0)

  useEffect(() => {
    loadListings()
  }, [])

  const loadListings = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const data = await ListingsService.getListings()
      setListings(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load listings')
    } finally {
      setIsLoading(false)
    }
  }

  const handleStatusChange = async (listingId: string, status: 'published' | 'paused') => {
    try {
      await ListingsService.updateListingStatus(listingId, status)
      // Update local state
      setListings(prev => prev.map(listing => 
        listing.listingId === listingId 
          ? { ...listing, status }
          : listing
      ))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update listing status')
    }
  }

  const handleDelete = async (listingId: string) => {
    try {
      await ListingsService.deleteListing(listingId)
      // Remove from local state
      setListings(prev => prev.filter(listing => listing.listingId !== listingId))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete listing')
    }
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  const filterListings = (status?: string) => {
    if (!status) return listings
    return listings.filter(listing => listing.status === status)
  }

  const allListings = listings
  const draftListings = filterListings('draft')
  const publishedListings = filterListings('published')
  const pausedListings = filterListings('paused')

  const inactiveListings = filterListings('inactive')

  if (isLoading) {
    return (
      <SidebarLayout>
        <LoadingSpinner />
      </SidebarLayout>
    )
  }

  return (
    <SidebarLayout>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 },
          mb: 4 
        }}>
          <Box>
            <Typography variant="h3" component="h1" gutterBottom>
              Listings
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your property listings
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            component={Link}
            to="/listings/new"
            sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
          >
            Add Listing
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {listings.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No listings yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first listing to get started
            </Typography>
            <Button
              variant="outlined"
              component={Link}
              to="/listings/new"
              startIcon={<Add />}
            >
              Create First Listing
            </Button>
          </Box>
        ) : (
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange} 
                variant="scrollable" 
                scrollButtons="auto"
                allowScrollButtonsMobile
              >
                <Tab label={`All (${allListings.length})`} />
                <Tab label={`Published (${publishedListings.length})`} />
                <Tab label={`Draft (${draftListings.length})`} />
                <Tab label={`Paused (${pausedListings.length})`} />

                {inactiveListings.length > 0 && (
                  <Tab label={`Pending Review (${inactiveListings.length})`} />
                )}
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={3}>
                {allListings.map((listing) => (
                  <Grid item xs={12} sm={6} lg={4} key={listing.listingId}>
                    <ListingCard
                      listing={listing}
                      onStatusChange={handleStatusChange}
                      onDelete={handleDelete}
                    />
                  </Grid>
                ))}
              </Grid>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Grid container spacing={3}>
                {publishedListings.map((listing) => (
                  <Grid item xs={12} sm={6} lg={4} key={listing.listingId}>
                    <ListingCard
                      listing={listing}
                      onStatusChange={handleStatusChange}
                      onDelete={handleDelete}
                    />
                  </Grid>
                ))}
              </Grid>
              {publishedListings.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No published listings yet
                  </Typography>
                </Box>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <Grid container spacing={3}>
                {draftListings.map((listing) => (
                  <Grid item xs={12} sm={6} lg={4} key={listing.listingId}>
                    <ListingCard
                      listing={listing}
                      onStatusChange={handleStatusChange}
                      onDelete={handleDelete}
                    />
                  </Grid>
                ))}
              </Grid>
              {draftListings.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No draft listings
                  </Typography>
                </Box>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              <Grid container spacing={3}>
                {pausedListings.map((listing) => (
                  <Grid item xs={12} sm={6} lg={4} key={listing.listingId}>
                    <ListingCard
                      listing={listing}
                      onStatusChange={handleStatusChange}
                      onDelete={handleDelete}
                    />
                  </Grid>
                ))}
              </Grid>
              {pausedListings.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No paused listings
                  </Typography>
                </Box>
              )}
            </TabPanel>

            {inactiveListings.length > 0 && (
              <TabPanel value={tabValue} index={4}>
                <Grid container spacing={3}>
                  {inactiveListings.map((listing) => (
                    <Grid item xs={12} sm={6} lg={4} key={listing.listingId}>
                      <ListingCard
                        listing={listing}
                        onStatusChange={handleStatusChange}
                        onDelete={handleDelete}
                      />
                    </Grid>
                  ))}
                </Grid>
                {inactiveListings.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No listings pending review
                    </Typography>
                  </Box>
                )}
              </TabPanel>
            )}
          </>
        )}
      </SidebarLayout>
  )
}